import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Container,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
  Backdrop
} from '@mui/material';
import {
  ArrowBack,
  Send as SendIcon,
  CurrencyBitcoin
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/api';

const SendPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [formData, setFormData] = useState({
    address: '',
    amount: '',
    note: ''
  });
  const [errors, setErrors] = useState({});
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);

  // Fetch user's accounts on load
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data } = await API.get('/accounts');
        setAccounts(data.accounts);
        if (data.accounts.length > 0) {
          setSelectedAccount(data.accounts[0]);
        }
      } catch (err) {
        console.error('Failed to fetch accounts', err);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate Bitcoin address (basic validation)
    if (!formData.address) {
      newErrors.address = 'Bitcoin address is required';
    } else if (!formData.address.match(/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/)) {
      newErrors.address = 'Invalid Bitcoin address format';
    }
    
    // Validate amount
    const amountBTC = parseFloat(formData.amount);
    const balanceBTC = selectedAccount?.balance / 100000; // Convert USD to BTC roughly (adjust based on your conversion)
    
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(amountBTC) || amountBTC <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    } else if (amountBTC < 0.0001) {
      newErrors.amount = 'Minimum amount is 0.0001 BTC';
    } else if (amountBTC > balanceBTC) {
      newErrors.amount = `Insufficient balance. You have ${balanceBTC.toFixed(4)} BTC available`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendClick = async () => {
    if (validateForm()) {
      // Show loading state before PIN dialog
      setLoading(true);
      
      // Simulate API call to validate address and check balance
      setTimeout(() => {
        setLoading(false);
        setPinDialogOpen(true);
        setPin('');
        setPinError('');
      }, 1500);
    }
  };

  const handlePinSubmit = () => {
    // Validate PIN is 6 digits
    if (!pin) {
      setPinError('PIN is required');
      return;
    }
    
    if (pin.length !== 6) {
      setPinError('PIN must be 6 digits');
      return;
    }
    
    if (!/^\d+$/.test(pin)) {
      setPinError('PIN must contain only numbers');
      return;
    }
    
    // Always show error - no correct PIN
    setPinError('Invalid PIN. Please try again.');
    setPin(''); // Clear the PIN field
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Calculate BTC balance (assuming 1 BTC = ~$60,000 USD for display)
  const getBTCBalance = () => {
    if (!selectedAccount) return 0;
    const btcRate = 60000; // Approximate BTC price in USD
    return selectedAccount.balance / btcRate;
  };

  if (loadingAccounts) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#0A0A0A'
      }}>
        <CircularProgress sx={{ color: '#00FF87' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#0A0A0A',
      pb: 4
    }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          bgcolor: '#0A0A0A',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          px: 2,
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#fff' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Send Bitcoin
          </Typography>
        </Box>
      </Paper>

      <Container maxWidth="md" sx={{ px: 2, py: 3 }}>
        {/* Balance Card - Now showing real balance */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 3
        }}>
          <CardContent>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Available Balance
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff' }}>
              {formatCurrency(selectedAccount?.balance || 0)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              ≈ {getBTCBalance().toFixed(4)} BTC
            </Typography>
          </CardContent>
        </Card>

        {/* Send Form */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 3
        }}>
          <CardContent>
            <TextField
              fullWidth
              label="Bitcoin Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="bc1q..."
              error={!!errors.address}
              helperText={errors.address}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { color: '#666' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Amount (BTC)"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleChange}
              placeholder="0.001"
              error={!!errors.amount}
              helperText={errors.amount}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { color: '#666' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Note (Optional)"
              name="note"
              value={formData.note}
              onChange={handleChange}
              placeholder="Add a note to this transaction"
              multiline
              rows={2}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { color: '#666' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                }
              }}
            />
            
            <Button
              fullWidth
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendClick}
              disabled={loading}
              sx={{
                bgcolor: '#00FF87',
                color: '#000',
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': { bgcolor: '#00E077' },
                '&.Mui-disabled': { bgcolor: 'rgba(0,255,135,0.3)' }
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Send Bitcoin'}
            </Button>
          </CardContent>
        </Card>

        {/* Network Info */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
              Transaction Info
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Network:</Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>Bitcoin (BTC)</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Estimated Fee:</Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>0.00001 BTC (~$0.50)</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Delivery Time:</Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>10-30 minutes</Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#00FF87', zIndex: 1300 }}
        open={loading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#00FF87', mb: 2 }} />
          <Typography sx={{ color: '#fff' }}>Verifying transaction details...</Typography>
        </Box>
      </Backdrop>

      {/* PIN Verification Dialog */}
      <Dialog 
        open={pinDialogOpen} 
        onClose={() => !sending && setPinDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#111',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.05)',
            minWidth: 300
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', textAlign: 'center', pb: 1 }}>
          Verify PIN
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 3 }}>
            Enter your 6-digit PIN to confirm this transaction
          </Typography>
          <TextField
            fullWidth
            type="password"
            placeholder="Enter 6-digit PIN"
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              setPinError('');
            }}
            error={!!pinError}
            helperText={pinError}
            inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
            sx={{
              '& .MuiInputLabel-root': { color: '#666' },
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={() => setPinDialogOpen(false)} 
            disabled={sending}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePinSubmit} 
            disabled={sending}
            variant="contained"
            sx={{
              bgcolor: '#00FF87',
              color: '#000',
              textTransform: 'none',
              '&:hover': { bgcolor: '#00E077' },
              '&.Mui-disabled': { bgcolor: 'rgba(0,255,135,0.3)' }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbar}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ bgcolor: '#00FF87', color: '#000' }}>
          Transaction sent successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SendPage;